/**
 * Premium text field used by every auth form.
 *
 * Departures from MUI's stock outlined TextField:
 *  - Warm-cream inset fill (not pure white) so the field reads as
 *    part of the ivory page surface, not a foreign control.
 *  - Hairline neutral border by default; champagne-gold border on
 *    hover; full gold on focus. No abrupt color jumps.
 *  - Inter throughout — matches the dashboard's section-heading
 *    typography system.
 *  - Larger touch target + generous internal padding for premium
 *    weight.
 */

import { TextField, type TextFieldProps } from '@mui/material';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_FOCUS = '#d4a84b';
const FONT_STACK = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function AuthTextField(props: TextFieldProps) {
  return (
    <TextField
      {...props}
      fullWidth
      variant="outlined"
      sx={{
        '& .MuiOutlinedInput-root': {
          fontFamily: FONT_STACK,
          fontSize: 14.5,
          bgcolor: 'rgba(255, 250, 235, 0.55)',
          transition: 'background-color 160ms ease, border-color 160ms ease',
          '& fieldset': {
            borderColor: 'rgba(15, 10, 7, 0.14)',
            borderWidth: 1,
            transition: 'border-color 160ms ease',
          },
          '&:hover': {
            bgcolor: 'rgba(255, 250, 235, 0.8)',
            '& fieldset': {
              borderColor: 'rgba(187, 140, 63, 0.45)',
            },
          },
          '&.Mui-focused': {
            bgcolor: '#fff',
            '& fieldset': {
              borderColor: GOLD_FOCUS,
              borderWidth: 1.5,
            },
          },
          '& input': {
            color: INK,
            py: 1.5,
            letterSpacing: 0.1,
          },
          '& input:-webkit-autofill': {
            WebkitTextFillColor: INK,
            WebkitBoxShadow: '0 0 0 100px rgba(255, 250, 235, 0.55) inset',
            caretColor: INK,
          },
        },
        '& .MuiInputLabel-root': {
          fontFamily: FONT_STACK,
          fontSize: 14,
          color: 'rgba(15, 10, 7, 0.55)',
          letterSpacing: 0.2,
          // Custom easing: matches the field bg + border transitions
          // exactly (180ms cubic-bezier) so the whole field "blooms"
          // as one composed unit on focus rather than each property
          // animating at its own MUI default rate.
          transition:
            'color 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.4, 0, 0.2, 1), font-weight 180ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-focused': {
            color: GOLD,
            fontWeight: 600,
          },
        },
        // Floating (shrunk) label state — slightly more upward lift
        // than MUI's default so the label feels like it's "stepping
        // out of the way" with intention.
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
          transform: 'translate(14px, -10px) scale(0.78)',
        },
        '& .MuiFormHelperText-root': {
          fontFamily: FONT_STACK,
          fontSize: 12.5,
          mt: 0.75,
          color: 'rgba(15, 10, 7, 0.55)',
          '&.Mui-error': {
            color: '#c0392b',
          },
        },
        ...props.sx,
      }}
    />
  );
}
